<template>
  <section class="scorers">
    <div class="scorers__team scorers__team--home">
      <div v-for="{ player, displayedGoals } in homeScorers" :key="`${player.playerId}-${displayedGoals}`" class="player">
        <p class="player__name" :class="{ 'player__name--removed': isNotScorerAnymore(player, displayedGoals) }">
          {{ player.lastName }}
        </p>
        <div class="goals">
          <goals-display :player="player" :displayed-goals="displayedGoals" />
        </div>
      </div>
    </div>
    <div class="scorers__team scorers__team--away">
      <div v-for="{ player, displayedGoals } in awayScorers" :key="`${player.playerId}-${displayedGoals}`" class="player">
        <p class="player__name" :class="{ 'player__name--removed': isNotScorerAnymore(player, displayedGoals) }">
          {{ player.lastName }}
        </p>
        <div class="goals">
          <goals-display :player="player" :displayed-goals="displayedGoals" reverse-display />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import GoalsDisplay from "@/components/goals/GoalsDisplay.vue";
import { OWN_GOALS_ONLY, SCORED_GOALS_ONLY } from "@/constants/displayedGoals";
import { Match } from "@/models/match/Match";
import { computed } from "vue";

const props = defineProps({
  match: {
    type: Match,
    required: true,
  },
});

/**
 * Buteurs d'une équipe : ses propres buteurs, puis les auteurs de CSC de l'équipe adverse
 * Un joueur ayant marqué et marqué contre son camp apparaît dans les 2 équipes,
 * avec seulement les buts correspondant à l'équipe affichée
 * @param {Team} team L'équipe dont on affiche les buteurs
 * @param {Team} opponentTeam L'équipe adverse
 * @returns {Array} la liste des buteurs à afficher et les buts à afficher pour chacun
 */
const getScorers = (team, opponentTeam) => {
  return [
    ...team.pitchPlayers
      .filter(({ goals, mpgGoals }) => goals + mpgGoals > 0)
      .map(player => ({
        player,
        displayedGoals: SCORED_GOALS_ONLY,
      })),
    ...opponentTeam.pitchPlayers
      .filter(({ ownGoals }) => ownGoals > 0)
      .map(player => ({
        player,
        displayedGoals: OWN_GOALS_ONLY,
      })),
  ];
};

const homeScorers = computed(() => getScorers(props.match.homeTeam, props.match.awayTeam));
const awayScorers = computed(() => getScorers(props.match.awayTeam, props.match.homeTeam));

/**
 * Vérifie si tous les buts affichés du joueur ont été annulés ou arrêtés
 * Un CSC ne peut être ni annulé ni arrêté
 */
const isNotScorerAnymore = (player, displayedGoals) => {
  return displayedGoals === SCORED_GOALS_ONLY
    && player.goals + player.mpgGoals - player.canceledGoals - player.savedGoals === 0;
};
</script>

<style lang="scss" scoped>
.scorers {
  display: flex;
  position: relative;
  z-index: 1;

  &__team {
    display: flex;
    flex-direction: column;
    width: 50%;

    &--home {
      padding-right: 2vw;
      .player {
        flex-direction: row;
        justify-content: flex-end;

        .goals {
          margin-left: 20px;
        }
      }
    }

    &--away {
      padding-left: 2vw;
      .player {
        flex-direction: row-reverse;
        justify-content: start;

        .goals {
          margin-right: 20px;
        }
      }
    }

    .player {
      display: flex;
      align-items: center;

      &__name {
        &--removed {
          text-decoration: line-through;
          text-decoration-color: rgb(232, 30, 41);
          text-decoration-thickness: 0.1rem;
        }
      }

      p {
        margin-bottom: 0;
      }
    }
  }
}
  </style>